import { useEffect } from "react";
import { Field, useForm, useFormState } from "react-final-form";
import { useIntl } from "react-intl";
import { LearningProgramConstants } from "../../../../constants";
import {
  DatePicker,
  DropdownInput,
  MultiSelectInput,
  SwitchInput,
  TextInput,
} from "../../../common/fields";
import * as Validations from "../../../common/form/validator";
import { withSelected } from "../../../common/hooks/useServerSearch";

const AssignmentDetailsForm = ({
  audiences,
  activeAssignments,
  progarms,
  recordId,
  // Searches the server as the admin types in the cohort picker
  onAudienceSearch,
}) => {
  const formState = useFormState();
  const intl = useIntl();
  const form = useForm();

  const isOptional = formState?.values?.is_optional === true;

  useEffect(() => {
    if (formState?.values?.is_optional) {
      form.change("is_flexible_date", false);
    } else if (formState?.values?.subject_to === 3) {
      form.change("is_flexible_date", true);
    }
  }, [formState?.values?.subject_to, formState?.values?.is_optional, form]);

  return (
    <div className="grid">
      <div className="col-12 md:col-6 lg:col-4">
        <Field
          name="subject_to"
          component={DropdownInput}
          label={intl.formatMessage({
            id: "assign_based_on",
          })}
          options={LearningProgramConstants.ASSIGN_ON}
          placeholder={intl.formatMessage({
            id: "assign_based_on",
          })}
          required
          validate={Validations.composeValidators(Validations.required)}
        />
      </div>
      {(formState?.values?.subject_to === 1 && (
        <div className="col-12 md:col-6 lg:col-4">
          <Field
            name="audience"
            component={MultiSelectInput}
            label={intl.formatMessage({
              id: "audience",
            })}
            options={withSelected(
              audiences.filter((item) => !activeAssignments?.includes(item.id)),
              // Cohorts already chosen, which a search may have left out
              formState?.values?.audience
            )}
            filter
            onFilter={onAudienceSearch}
            placeholder={intl.formatMessage({
              id: "audience",
            })}
            required
            validate={Validations.composeValidators(Validations.required)}
          />
        </div>
      )) ||
        null}
      {(formState?.values?.subject_to === 3 && (
        <div className="col-12 md:col-6 lg:col-4">
          <Field
            name="program"
            component={DropdownInput}
            label={intl.formatMessage({
              id: "learning_program",
            })}
            options={progarms?.filter(
              (item) =>
                item.id !== recordId && !activeAssignments?.includes(item.id)
            )}
            placeholder={intl.formatMessage({
              id: "learning_program",
            })}
            required
            validate={Validations.composeValidators(Validations.required)}
          />
        </div>
      )) ||
        null}
      <div className="col-12 md:col-6 lg:col-4">
        <Field
          name="is_optional"
          component={DropdownInput}
          label={intl.formatMessage({
            id: "training_type",
          })}
          options={LearningProgramConstants.TRAINING_TYPES}
          placeholder={intl.formatMessage({
            id: "training_type",
          })}
          required
        />
      </div>
      {(formState?.values?.subject_to === 3 && (
        <div className="col-12 md:col-6 lg:col-4">
          <Field
            name="reenrol_if_completed"
            component={SwitchInput}
            type="checkbox"
            label={intl.formatMessage({
              id: "reenrol_if_completed",
            })}
          />
        </div>
      )) ||
        null}
      {(!isOptional && (
        <div className="col-12 md:col-6 lg:col-4">
          <Field
            name="is_flexible_date"
            component={SwitchInput}
            type="checkbox"
            label={intl.formatMessage({
              id: "is_date_flexible",
            })}
            disabled={formState?.values?.subject_to === 3}
            placeholder={intl.formatMessage({
              id: "is_date_flexible",
            })}
          />
        </div>
      )) ||
        null}
      {(!isOptional && formState?.values?.is_flexible_date && (
        <div className="col-12 md:col-6 lg:col-4">
          <Field
            name="relative_end_days"
            component={TextInput}
            type="number"
            label={intl.formatMessage({
              id: "relative_end_days",
            })}
            placeholder={intl.formatMessage({
              id: "relative_end_days",
            })}
            required
            validate={Validations.composeValidators(
              Validations.required,
              Validations.numberGreater0
            )}
          />
        </div>
      )) ||
        null}
      {((isOptional || !formState?.values?.is_flexible_date) && (
        <>
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="start_date"
              component={DatePicker}
              label={intl.formatMessage({
                id: "start_date",
              })}
              options={[]}
              placeholder={intl.formatMessage({
                id: "start_date_place_holder",
              })}
              required
              minDate={new Date()}
              validate={Validations.composeValidators(Validations.required)}
            />
          </div>
          {(!isOptional && (
            <div className="col-12 md:col-6 lg:col-4">
              <Field
                name="end_date"
                component={DatePicker}
                label={intl.formatMessage({
                  id: "end_date",
                })}
                options={[]}
                placeholder={intl.formatMessage({
                  id: "end_date",
                })}
                minDate={formState?.values?.start_date || new Date()}
                validate={Validations.composeValidators(
                  Validations.minDate("start_date")
                )}
              />
            </div>
          )) ||
            null}
          <div className="col-12 md:col-6 lg:col-4"></div>
          <div className="col-12 md:col-6 lg:col-4">
            <Field
              name="timezone"
              filter={true}
              component={DropdownInput}
              label={intl.formatMessage({
                id: "timezone",
              })}
              options={Intl.supportedValuesOf("timeZone").map((item) => ({
                id: item,
                name: item,
              }))}
              placeholder={intl.formatMessage({
                id: "timezone",
              })}
              required
              validate={Validations.composeValidators(Validations.required)}
            />
          </div>
        </>
      )) ||
        null}
    </div>
  );
};

export default AssignmentDetailsForm;
